import type { UseFormRegister } from 'react-hook-form'
import { getFieldLabel, getCountries, getCountryStates, isCountryUSOrCA, isFieldRequired } from '@utils/validation'

type Props = {
  customer: any
  customerErrors: Record<string, any>
  registerCustomer: UseFormRegister<any>
  setCustomerValue: (field: any, value: any, options?: any) => void
  showBillEdit: boolean
  isSessionUserKnown: () => boolean
  toggleEditing: (name: 'bill' | 'ship' | 'source') => void
  countryChanged: () => void
  zipFormatHandler: (value: string, isShip?: boolean) => string
}

/**
 * Billing address. Read-only for a known customer until Edit is pressed; the
 * same button then saves, which is why `toggleEditing` is async upstream.
 */
const CheckoutBillingSection = ({
  customer,
  customerErrors,
  registerCustomer,
  setCustomerValue,
  showBillEdit,
  isSessionUserKnown,
  toggleEditing,
  countryChanged,
  zipFormatHandler,
}: Props) => (
  <div className="form-section">
    <div className="flex justify-between items-center mb-4">
      <h4 className="m-0!">Billing Information</h4>
      {isSessionUserKnown() && (
        <button type="button" className="btn-bare checkout-change-button" onClick={() => toggleEditing('bill')}>
          {showBillEdit ? 'SAVE' : 'EDIT'}
        </button>
      )}
    </div>

    {!showBillEdit ? (
      <div className="checkout-info-card">
        <p>
          {customer.first_name} {customer.last_name}
          <br />
          {customer.address1}
          {customer.address2 ? (
            <>
              <br />
              {customer.address2}
            </>
          ) : null}
          <br />
          {customer.city}
          {customer.state ? `, ${customer.state}` : ''}, {customer.country}, {customer.zip}
          <br />
          {customer.email}
          <br />
          {customer.phone}
        </p>
      </div>
    ) : (
      <div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3">
          <div className="form-group">
            <input
              type="text"
              className={`form-control ${customerErrors.first_name ? 'is-invalid' : ''}`}
              placeholder={getFieldLabel('first_name', 'First Name', customer.country)}
              {...registerCustomer('first_name')}
            />
            {customerErrors.first_name && <div className="invalid-feedback">{customerErrors.first_name.message}</div>}
          </div>
          <div className="form-group">
            <input
              type="text"
              className={`form-control ${customerErrors.last_name ? 'is-invalid' : ''}`}
              placeholder={getFieldLabel('last_name', 'Last Name', customer.country)}
              {...registerCustomer('last_name')}
            />
            {customerErrors.last_name && <div className="invalid-feedback">{customerErrors.last_name.message}</div>}
          </div>
        </div>

        {isFieldRequired('company', customer.country) && (
          <div className="form-group">
            <input
              type="text"
              className={`form-control ${customerErrors.company ? 'is-invalid' : ''}`}
              placeholder={getFieldLabel('company', 'Company', customer.country)}
              {...registerCustomer('company')}
            />
            {customerErrors.company && <div className="invalid-feedback">{customerErrors.company.message}</div>}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3">
          <div className="form-group">
            <input
              type="email"
              className={`form-control ${customerErrors.email ? 'is-invalid' : ''}`}
              placeholder="Email"
              {...registerCustomer('email')}
            />
            {customerErrors.email && <div className="invalid-feedback">{customerErrors.email.message}</div>}
          </div>
          <div className="form-group">
            <input
              type="tel"
              inputMode="numeric"
              className={`form-control ${customerErrors.phone ? 'is-invalid' : ''}`}
              placeholder="Phone"
              value={customer.phone}
              onChange={e => {
                const digits = e.target.value.replace(/[^0-9+\-() ]/g, '')
                setCustomerValue('phone', digits, { shouldValidate: true })
              }}
            />
            {customerErrors.phone && <div className="invalid-feedback">{customerErrors.phone.message}</div>}
          </div>
        </div>

        <div className="form-group">
          <input
            type="text"
            className={`form-control ${customerErrors.address1 ? 'is-invalid' : ''}`}
            placeholder={getFieldLabel('address1', 'Address', customer.country)}
            {...registerCustomer('address1')}
          />
          {customerErrors.address1 && <div className="invalid-feedback">{customerErrors.address1.message}</div>}
        </div>

        <div className="form-group">
          <input
            type="text"
            className="form-control"
            placeholder={getFieldLabel('address2', 'Apartment, suite, etc. (optional)', customer.country)}
            {...registerCustomer('address2')}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-4 gap-x-3">
          <div className="form-group sm:col-span-2">
            <input
              type="text"
              className={`form-control ${customerErrors.city ? 'is-invalid' : ''}`}
              placeholder={getFieldLabel('city', 'City', customer.country)}
              {...registerCustomer('city')}
            />
            {customerErrors.city && <div className="invalid-feedback">{customerErrors.city.message}</div>}
          </div>
          {isCountryUSOrCA(customer.country) ? (
            <div className="form-group">
              <select
                className={`form-control ${customerErrors.state ? 'is-invalid' : ''}`}
                {...registerCustomer('state')}>
                <option value="">{getFieldLabel('state', 'State', customer.country)}</option>
                {getCountryStates(customer.country).map(state => (
                  <option key={state.value} value={state.value}>
                    {state.text}
                  </option>
                ))}
              </select>
              {customerErrors.state && <div className="invalid-feedback">{customerErrors.state.message}</div>}
            </div>
          ) : (
            <div className="form-group">
              <input
                type="text"
                className={`form-control ${customerErrors.state ? 'is-invalid' : ''}`}
                placeholder="State / Province"
                {...registerCustomer('state')}
              />
            </div>
          )}
          <div className="form-group">
            <input
              type="text"
              className={`form-control ${customerErrors.zip ? 'is-invalid' : ''}`}
              placeholder={getFieldLabel('zip', 'ZIP code', customer.country)}
              value={customer.zip}
              onChange={e => setCustomerValue('zip', zipFormatHandler(e.target.value), { shouldValidate: true })}
            />
            {customerErrors.zip && <div className="invalid-feedback">{customerErrors.zip.message}</div>}
          </div>
        </div>

        <div className="form-group">
          <select className="form-control" {...registerCustomer('country', { onChange: countryChanged })}>
            {getCountries().map(country => (
              <option key={country.value} value={country.value}>
                {country.text}
              </option>
            ))}
          </select>
        </div>
      </div>
    )}
  </div>
)

export default CheckoutBillingSection
